import {
  ReturnJobPositionReadable,
  seniorPositionsJobPositionsCreateMutation,
  seniorPositionsJobPositionsDestroyMutation,
  seniorPositionsJobPositionsListOptions,
  seniorPositionsJobPositionsListQueryKey,
  seniorPositionsJobPositionsPartialUpdateMutation,
  seniorPositionsJobPositionsRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import { FormNomenclaturesJobPositions } from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import {
  DayLabel,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form, MenuProps, Modal, TableColumnsType } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

export const NomenclaturesJobPositions = () => {
  const { id } = useParams()
  const { t } = useLocales()
  const params = useQueryParams()
  const { notification } = App.useApp()
  const { resetPagination } = useResetPagination()

  const sorter = useTableSorter()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)

  const [form] = Form.useForm()

  const { data: jobPositions, isLoading } = useQuery({
    ...seniorPositionsJobPositionsListOptions({
      query: { ...defaultFilters, ...params },
      path: { senior_position_id: String(id) }
    }),
    enabled: !!id
  })

  const { data: jobPosition, isLoading: isLoadingJobPosition } = useQuery({
    ...seniorPositionsJobPositionsRetrieveOptions({
      path: { id: Number(editItem), senior_position_id: String(id) }
    }),
    enabled: !!editItem
  })

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnJobPositionReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'title',
        key: 'title',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
    form.resetFields()
  }

  const deleteJobPositionMutation = useMutation({
    ...seniorPositionsJobPositionsDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      resetPagination()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const createJobPositionMutation = useMutation({
    ...seniorPositionsJobPositionsCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const updateJobPositionMutation = useMutation({
    ...seniorPositionsJobPositionsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSuccessMutation = () => {
    invalidateQueries([
      seniorPositionsJobPositionsListQueryKey({
        path: { senior_position_id: String(id) }
      })
    ])
    onCloseModal()
  }

  const onDelete = () =>
    deleteItem &&
    deleteJobPositionMutation.mutate({
      path: { id: deleteItem, senior_position_id: String(id) }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    return editItem
      ? updateJobPositionMutation.mutate({
          path: { id: editItem, senior_position_id: String(id) },
          body: values
        })
      : createJobPositionMutation.mutate({
          body: values,
          path: { senior_position_id: String(id) }
        })
  }

  useEffect(() => {
    if (jobPosition) {
      form.setFieldsValue(jobPosition)
    }
  }, [jobPosition])

  return (
    <>
      <TitleSection title={t('nomenclature.job_positions')} />

      <TableContainer
        data={jobPositions}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_job_position')}
          </Button>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <Modal
        open={!!(editItem ?? isCreate)}
        title={t(!id ? 'nomenclature.add_entity' : 'nomenclature.edit_entity')}
        onCancel={onCloseModal}
        loading={isLoadingJobPosition}
        okButtonProps={{
          htmlType: 'submit',
          loading:
            updateJobPositionMutation.isPending ||
            createJobPositionMutation.isPending,
          onClick: onSubmit
        }}
      >
        <Form form={form} layout="vertical">
          <FormNomenclaturesJobPositions />
        </Form>
      </Modal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteJobPositionMutation.isPending}
      />
    </>
  )
}
